import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Briefcase, Gift, MapPin, Target, UserRound } from 'lucide-react';
import { fetchPublishedJobBySlug } from '@/services/careerService';
import { careerCopy } from '@/types/career';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import JobApplyDialog from '@/components/careers/JobApplyDialog';
import JobLocationMap from '@/components/careers/JobLocationMap';

const JobDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, tl } = useLanguage();
  const [applyOpen, setApplyOpen] = useState(false);

  const { data: job, isLoading, error } = useQuery({
    queryKey: ['careers', 'published', slug],
    queryFn: () => fetchPublishedJobBySlug(slug as string),
    enabled: Boolean(slug),
  });

  const title = job ? careerCopy(job.title, language) : '';

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">
        {tl('Chargement…', 'Loading…', 'Caricamento…')}
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="container-custom px-4 py-20 text-center">
        <h1 className="text-2xl font-semibold">
          {tl('Offre introuvable', 'Role not found', 'Offerta non trovata')}
        </h1>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/careers">
            {tl('Retour aux carrières', 'Back to careers', 'Torna alle carriere')}
          </Link>
        </Button>
      </div>
    );
  }

  const sections = [
    {
      key: 'missions',
      icon: Target,
      title: tl('Ce que vous ferez', 'What you will do', 'Cosa farai'),
      body: job.missions,
    },
    {
      key: 'profile',
      icon: UserRound,
      title: tl('Profil recherché', 'Profile we look for', 'Profilo ricercato'),
      body: job.profile,
    },
    {
      key: 'offer',
      icon: Gift,
      title: tl('Ce que nous offrons', 'What we offer', 'Cosa offriamo'),
      body: job.offer,
    },
  ].filter((section) => section.body?.trim());

  return (
    <div className="min-h-screen bg-background pb-28 md:pb-16">
      <Helmet>
        <title>{title} | Meubles Karim</title>
      </Helmet>

      <section className="relative h-[36vh] min-h-[220px] overflow-hidden md:h-[44vh]">
        <img
          src={job.coverImage || '/placeholder.svg'}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 container-custom flex flex-col justify-end px-4 pb-8 md:pb-12">
          <Link
            to="/careers"
            className="mb-4 inline-flex w-fit items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {tl('Carrières', 'Careers', 'Carriere')}
          </Link>
          <h1 className="max-w-3xl text-3xl font-bold text-white md:text-5xl">{title}</h1>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wider text-white/85">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
              <Briefcase className="h-3 w-3" />
              {job.employmentType}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
              <MapPin className="h-3 w-3" />
              {job.location}
            </span>
            {job.department ? (
              <span className="rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">
                {job.department}
              </span>
            ) : null}
          </div>
        </div>
      </section>

      <div className="container-custom grid gap-8 px-4 py-10 md:grid-cols-[1fr_280px] md:py-14 lg:grid-cols-[1fr_300px]">
        <article className="space-y-8">
          {careerCopy(job.shortDescription, language)?.trim() ? (
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              {careerCopy(job.shortDescription, language)}
            </p>
          ) : null}

          {careerCopy(job.description, language)?.trim() ? (
            <section className="space-y-3">
              <h2 className="text-xl font-semibold md:text-2xl">
                {tl('Description', 'Description', 'Descrizione')}
              </h2>
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground md:text-base">
                {careerCopy(job.description, language)}
              </div>
            </section>
          ) : null}

          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <section
                key={section.key}
                className="space-y-3 rounded-2xl border border-border/70 bg-white p-5 shadow-soft md:p-6"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-furniture-gold/15 text-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h2 className="text-lg font-semibold md:text-xl">{section.title}</h2>
                </div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground md:text-base">
                  {section.body}
                </div>
              </section>
            );
          })}

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-foreground" />
              <h2 className="text-lg font-semibold md:text-xl">
                {tl('Localisation', 'Location', 'Località')}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">{job.location}</p>
            <JobLocationMap
              location={job.location}
              lat={job.mapLat}
              lng={job.mapLng}
            />
          </section>
        </article>

        <aside className="hidden md:block">
          <div className="sticky top-28 space-y-4 rounded-2xl border border-border/70 bg-white p-5 shadow-soft">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {tl('Candidature', 'Application', 'Candidatura')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {tl(
                'Envoyez votre CV et vos informations en quelques minutes.',
                'Send your CV and details in a few minutes.',
                'Invii il CV e i suoi dati in pochi minuti.'
              )}
            </p>
            <Button
              className="h-11 w-full bg-furniture-gold text-black hover:bg-furniture-gold-hover"
              onClick={() => setApplyOpen(true)}
            >
              {tl('Postuler', 'Apply', 'Candidati')}
            </Button>
          </div>
        </aside>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-white/95 p-3 backdrop-blur md:hidden">
        <Button
          className="h-12 w-full bg-furniture-gold text-black hover:bg-furniture-gold-hover"
          onClick={() => setApplyOpen(true)}
        >
          {tl('Postuler', 'Apply', 'Candidati')}
        </Button>
      </div>

      <JobApplyDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        jobId={job.id}
        jobTitle={title}
      />
    </div>
  );
};

export default JobDetails;
